/**
 * Chat Agent Module — Types
 *
 * Defines all types for agent configuration, runs, and transcript events.
 * Aligned with 50_Chat_Agent_Module_Spec_v0.1.md
 */

// ────────────────────────────────────────────────────────────────
// §2 AgentConfig
// ────────────────────────────────────────────────────────────────

export type AgentProviderApiMode = 'chat-completions' | 'responses'

export interface AgentProviderConfig {
    type: 'openai-chat-completions'
    apiKey: string
    baseUrl?: string
    apiMode?: AgentProviderApiMode
    useProxy?: boolean
    reasoningEffort?: 'low' | 'medium' | 'high'
    /**
     * Replay prior Responses reasoning as <thinking>...</thinking> inside assistant messages
     * instead of resending standalone reasoning items.
     */
    responsesReplayReasoningAsThinkingTokens?: boolean
    /**
     * Provider plugins — extend model capabilities per-request.
     * Known IDs for compatible providers include 'web', 'pdf', and 'response-healing'.
     */
    plugins?: Array<{
        id: string
        enabled?: boolean
        [key: string]: unknown
    }>
    /**
     * Provider-specific message transforms, e.g. ["middle-out"] when supported.
     */
    transforms?: string[]
}

export interface AgentMcpServerConfig {
    type?: string
    enabled?: boolean
    proxy?: boolean
    url?: string
    serverUrl?: string
    headers?: Record<string, string>
    bearerToken?: string
    tools?: Record<string, AgentMcpToolConfig | boolean>
    [key: string]: unknown
}

export interface AgentMcpToolConfig {
    enabled?: boolean
    [key: string]: unknown
}

export interface NormalizedAgentMcpServerConfig {
    url: string
    headers?: Record<string, string>
    proxy?: boolean
    upstreamUrl?: string
}

export interface AgentToolsConfig {
    secureBlob?: {
        read?: boolean
        write?: boolean
        confirmDestructive?: boolean
        enabled?: boolean
        tools?: Record<string, AgentMcpToolConfig | boolean>
    }
}

export interface AgentLimits {
    /** Preferred hard cap. Runtime must stop when reached. */
    maxSpendUsd?: string
    /** Backwards-compatible alias used by older configs/UI. */
    softSpendUsd?: string
}

export interface AgentConfig {
    v: '1'
    type: 'chat-agent'
    name: string
    description?: string
    provider: AgentProviderConfig
    model: string
    systemPrompt?: string
    defaultMode?: 'run' | 'edit'
    limits?: AgentLimits
    tools?: { secureBlob?: AgentToolsConfig['secureBlob'] }
    secrets?: string[]
    mcp?: Record<string, AgentMcpServerConfig | string>
    mcpServers?: Record<string, AgentMcpServerConfig | string>
}

// ────────────────────────────────────────────────────────────────
// §4 Transcript Events (interleaved stream)
// ────────────────────────────────────────────────────────────────

/** A file or image attached to a user message */
export interface Attachment {
    path: string        // workspace path in run folder
    mime: string        // MIME type
    size: number        // bytes
    name: string        // original filename
}

export interface ResolvedRef {
    path: string
    content: string | null   // null if file not found
    size?: number
    lineStart?: number       // 1-based, from @/file.md:L10-L25
    lineEnd?: number         // 1-based inclusive
    /** Image refs: base64 data URL for vision input */
    binaryBase64?: string
    /** True if this ref points to an image file */
    isImage?: boolean
    /** MIME type when isImage is true */
    mime?: string
}

export interface FileModification {
    path: string
    action: 'created' | 'updated' | 'deleted'
}

export interface AssistantToolBatchItem {
    id: string
    name: string
    args: Record<string, unknown>
    responseId?: string
    status?: string
}

/** Discriminated union — every event in the interleaved transcript stream */
export type TranscriptEvent =
    | { type: 'user_message'; text: string; refs: ResolvedRef[]; attachments?: Attachment[]; timestamp: number }
    | { type: 'assistant_thinking'; text?: string; timestamp: number }
    | { type: 'assistant_stream_delta'; text: string; timestamp: number }
    | {
        type: 'assistant_message_final'
        text: string
        id?: string
        status?: string
        phase?: 'commentary' | 'final_answer'
        content?: ResponseConversationContentPart[]
        timestamp: number
    }
    | { type: 'assistant_tool_batch'; items: AssistantToolBatchItem[]; timestamp: number }
    | { type: 'assistant_reasoning_item'; id: string; encryptedContent?: string; summary?: string[]; status?: string; timestamp: number }
    | { type: 'assistant_image'; dataUrl: string; savedPath: string; promotedPath?: string; timestamp: number }
    | { type: 'tool_call'; id: string; name: string; args: Record<string, unknown>; status: 'pending' | 'running' | 'completed' | 'error'; timestamp: number }
    | { type: 'tool_result'; id: string; name: string; result: string; filesModified?: FileModification[]; timestamp: number }
    | { type: 'error'; message: string; retryable: boolean; timestamp: number }
    | { type: 'cost_update'; tokensIn: number; tokensOut: number; estimatedCostUsd: number; timestamp: number }

// ────────────────────────────────────────────────────────────────
// §3 AgentRun
// ────────────────────────────────────────────────────────────────

export type AgentRunStatus = 'idle' | 'running' | 'stopping' | 'completed' | 'error' | 'cancelled'

export interface CostInfo {
    tokensIn: number
    tokensOut: number
    estimatedCostUsd: number
}

export interface AgentRun {
    runId: string
    status: AgentRunStatus
    transcript: TranscriptEvent[]
    cost: CostInfo
    createdAt: number
    completedAt?: number
}

// ────────────────────────────────────────────────────────────────
// Runtime controller
// ────────────────────────────────────────────────────────────────

export interface HistoryMessage {
    role: 'user' | 'assistant' | 'tool'
    content: string | null | HistoryContentPart[]
    tool_calls?: Array<{ id: string; function: { name: string; arguments: string } }>
    tool_call_id?: string
    name?: string
}

export type HistoryContentPart =
    | { type: 'text'; text: string }
    | { type: 'image_url'; image_url: { url: string } }
    | { type: 'file'; file: { filename: string; file_data: string } }
    | { type: 'input_audio'; input_audio: { data: string; format: string } }
    | { type: 'video_url'; video_url: { url: string } }

export type ResponseConversationContentPart =
    | { type: 'input_text'; text: string }
    | { type: 'output_text'; text: string; annotations?: unknown[] }
    | { type: 'input_image'; image_url: string; detail?: 'auto' | 'low' | 'high'; file_id?: string }
    | { type: 'input_file'; file_data?: string; file_id?: string; file_url?: string; filename?: string }

export interface ResponseConversationMessageItem {
    type: 'message'
    role: 'user' | 'assistant'
    id?: string
    status?: string
    phase?: 'commentary' | 'final_answer'
    content: ResponseConversationContentPart[]
}

export interface ResponseConversationFunctionCallItem {
    type: 'function_call'
    id?: string
    call_id: string
    name: string
    arguments: string
    status?: string
}

export interface ResponseConversationFunctionCallOutputItem {
    type: 'function_call_output'
    id?: string
    call_id: string
    output: string
}

export interface ResponseConversationReasoningItem {
    type: 'reasoning'
    id?: string
    encrypted_content?: string
    summary?: Array<{ type: 'summary_text'; text: string }>
    status?: string
}

export type ResponseConversationItem =
    | ResponseConversationMessageItem
    | ResponseConversationFunctionCallItem
    | ResponseConversationFunctionCallOutputItem
    | ResponseConversationReasoningItem

export type AgentConversationHistory =
    | { apiMode: 'chat-completions'; messages: HistoryMessage[] }
    | { apiMode: 'responses'; items: ResponseConversationItem[] }

export interface AgentRunController {
    /** Start streaming a run. Yields transcript events as they occur. */
    run(userMessage: string, refs: ResolvedRef[], attachments?: Attachment[], history?: AgentConversationHistory): AsyncIterableIterator<TranscriptEvent>
    /** Abort the current run */
    abort(): void
}

// ────────────────────────────────────────────────────────────────
// Config parsing
// ────────────────────────────────────────────────────────────────

/** Parse and validate an AgentConfig from JSON string. Returns null on invalid. */
export function parseAgentConfig(json: string): AgentConfig | null {
    try {
        const obj = JSON.parse(json)
        if (
            obj?.v !== '1' ||
            obj?.type !== 'chat-agent' ||
            typeof obj?.name !== 'string' ||
            typeof obj?.provider?.type !== 'string' ||
            typeof obj?.model !== 'string'
        ) {
            return null
        }
        return obj as AgentConfig
    } catch {
        return null
    }
}

export function getProviderApiMode(provider: AgentProviderConfig | undefined): AgentProviderApiMode {
    return provider?.apiMode === 'responses' ? 'responses' : 'chat-completions'
}

export function getNormalizedMcpServers(config: AgentConfig): Record<string, NormalizedAgentMcpServerConfig> {
    const rawServers = {
        ...(config.mcpServers ?? {}),
        ...(config.mcp ?? {}),
    }
    if (Object.keys(rawServers).length === 0) return {}

    const normalized: Record<string, NormalizedAgentMcpServerConfig> = {}
    for (const [name, entry] of Object.entries(rawServers)) {
        if (!isMcpServerEnabled(entry)) continue

        if (typeof entry === 'string') {
            const url = entry.trim()
            if (url) normalized[name] = { url }
            continue
        }
        if (!entry || typeof entry !== 'object') continue

        const url = typeof entry.url === 'string'
            ? entry.url.trim()
            : typeof entry.serverUrl === 'string'
                ? entry.serverUrl.trim()
                : ''
        if (!url) continue

        const headers = entry.headers && typeof entry.headers === 'object'
            ? Object.fromEntries(
                Object.entries(entry.headers).filter((pair): pair is [string, string] => typeof pair[1] === 'string')
            )
            : undefined

        const bearerToken = typeof entry.bearerToken === 'string' ? entry.bearerToken.trim() : ''
        const proxy = entry.proxy === true
        const resolvedUrl = proxy ? buildMcpProxyUrl(url) : url
        normalized[name] = {
            url: resolvedUrl,
            headers: bearerToken
                ? { ...(headers ?? {}), Authorization: headers?.Authorization ?? `Bearer ${bearerToken}` }
                : headers,
            proxy,
            upstreamUrl: proxy ? url : undefined,
        }
    }

    return normalized
}

export function buildResponsesProxyUrl(targetUrl: string): string {
    const trimmed = targetUrl.trim()
    if (!trimmed) return "/api/responses-proxy"

    const encoded = encodeBase64Url(trimmed)
    return `/api/responses-proxy/${encoded}`
}
export function buildMcpProxyUrl(targetUrl: string): string {
    const trimmed = targetUrl.trim()
    if (!trimmed) return '/api/mcp-proxy'

    const encoded = encodeBase64Url(trimmed)
    return `/api/mcp-proxy/${encoded}`
}

function encodeBase64Url(value: string): string {
    const bytes = new TextEncoder().encode(value)
    let binary = ''
    for (const byte of bytes) {
        binary += String.fromCharCode(byte)
    }
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '')
}

export function isMcpServerEnabled(entry: AgentMcpServerConfig | string | undefined): boolean {
    if (!entry || typeof entry === 'string') return true
    return entry.enabled !== false
}

export function isMcpToolEnabled(
    entry: AgentMcpServerConfig | string | undefined,
    toolName: string,
): boolean {
    if (!entry || typeof entry === 'string' || !entry.tools) return true
    const toolConfig = entry.tools[toolName]
    if (typeof toolConfig === 'boolean') return toolConfig
    if (!toolConfig || typeof toolConfig !== 'object') return true
    return toolConfig.enabled !== false
}

export function collectAgentSecretValues(config: AgentConfig): string[] {
    const values = new Set<string>()

    const addSecret = (value: unknown): void => {
        if (typeof value !== 'string') return
        const trimmed = value.trim()
        if (!trimmed || trimmed === '***REDACTED***') return
        values.add(trimmed)
    }

    addSecret(config.provider?.apiKey)

    const addSecretsFromServerMap = (servers: AgentConfig['mcp'] | AgentConfig['mcpServers']): void => {
        if (!servers) return
        for (const entry of Object.values(servers)) {
            if (typeof entry === 'string') {
                addSecretsFromUrl(entry, addSecret)
                continue
            }
            if (!entry || typeof entry !== 'object') continue
            addSecret(entry.bearerToken)
            addSecretsFromUrl(typeof entry.url === 'string' ? entry.url : undefined, addSecret)
            addSecretsFromUrl(typeof entry.serverUrl === 'string' ? entry.serverUrl : undefined, addSecret)
            if (entry.headers && typeof entry.headers === 'object') {
                for (const [key, value] of Object.entries(entry.headers)) {
                    addSecret(value)
                    if (/^authorization$/i.test(key) && typeof value === 'string') {
                        const match = value.match(/^\s*Bearer\s+(.+)\s*$/i)
                        if (match) addSecret(match[1])
                    }
                }
            }
        }
    }

    addSecretsFromServerMap(config.mcpServers)
    addSecretsFromServerMap(config.mcp)

    if (Array.isArray(config.secrets)) {
        for (const path of config.secrets) {
            addSecret(readStringAtPath(config, path))
        }
    }

    return [...values]
}

/** Mask secrets from an AgentConfig for model-visible context */
export function maskSecrets(config: AgentConfig): AgentConfig {
    let serialized = JSON.stringify(config)
    for (const secret of collectAgentSecretValues(config)) {
        serialized = serialized.split(secret).join('***REDACTED***')
    }
    return JSON.parse(serialized) as AgentConfig
}

function readStringAtPath(obj: unknown, path: string): string | null {
    if (!path) return null
    let current: unknown = obj
    for (const part of path.split('.')) {
        if (!current || typeof current !== 'object' || !(part in current)) return null
        current = (current as Record<string, unknown>)[part]
    }
    return typeof current === 'string' ? current : null
}

function addSecretsFromUrl(urlValue: string | undefined, addSecret: (value: unknown) => void): void {
    if (!urlValue) return
    try {
        const url = new URL(urlValue)
        for (const [key, value] of url.searchParams.entries()) {
            if (/(key|token|secret|auth)/i.test(key)) {
                addSecret(value)
            }
        }
    } catch {
        // Ignore invalid URLs in best-effort secret collection.
    }
}
